<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useUserStore } from '@/stores/user'
import { useUIStore } from '@/stores/ui'
import SearchPalette from '@/components/SearchPalette.vue'
import BackToTop from '@/components/BackToTop.vue'
import { absUrl, SITE_NAME, DEFAULT_DESC, useJsonLd } from '@/utils/seo'

const userStore = useUserStore()
const uiStore = useUIStore()

// 全站结构化数据：站点 + 组织信息（schema.org）
useJsonLd('ld-website', () => ({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      '@id': absUrl('/#organization'),
      name: SITE_NAME,
      url: absUrl('/'),
      logo: absUrl('/favicon.svg'),
    },
    {
      '@type': 'WebSite',
      '@id': absUrl('/#website'),
      url: absUrl('/'),
      name: SITE_NAME,
      description: DEFAULT_DESC,
      inLanguage: 'zh-CN',
      publisher: { '@id': absUrl('/#organization') },
    },
  ],
}))

/**
 * 应用启动时若已有 token 主动拉取一次用户信息
 */
onMounted(() => {
  if (userStore.token && !userStore.profile) {
    userStore.fetchProfile()
  }
})

const siteName = import.meta.env.VITE_SITE_NAME || 'Prompt Gallery'

function handleLogout() {
  userStore.logout()
  window.location.reload()
}

// 用户菜单 hover 状态：用 setTimeout 弥合头像与面板之间的空隙，避免鼠标移不到面板
const userMenuOpen = ref(false)
let userMenuTimer: ReturnType<typeof setTimeout> | null = null

// 移动端左侧抽屉菜单
const mobileMenuOpen = ref(false)

function openMobileMenu() {
  mobileMenuOpen.value = true
}

function closeMobileMenu() {
  mobileMenuOpen.value = false
}

function handleMobileLogout() {
  closeMobileMenu()
  handleLogout()
}

function openUserMenu() {
  if (userMenuTimer) {
    clearTimeout(userMenuTimer)
    userMenuTimer = null
  }
  userMenuOpen.value = true
}

function closeUserMenu() {
  userMenuTimer = setTimeout(() => {
    userMenuOpen.value = false
  }, 150)
}
</script>

<template>
  <div class="min-h-screen flex flex-col">
    <!-- 顶部导航 -->
    <header class="sticky top-0 z-30 backdrop-blur-sm"
      style="background-color: rgba(43, 43, 43, .3);border: 1px solid rgba(255, 255, 255, 0.1);">
      <div class="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
        <!-- 左侧：移动端汉堡按钮，桌面端 LOGO -->
        <div class="flex items-center gap-3">
          <button
            class="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-slate-200 transition hover:bg-white/10 md:hidden"
            aria-label="打开菜单" @click="openMobileMenu">
            <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>

          <RouterLink to="/" class="items-center gap-2 flex">
            <span
              class="grid h-9 w-9 place-items-center rounded-lg bg-black font-bold text-white shadow-lg shadow-gray-800">PG</span>
            <span class="text-lg font-semibold">{{ siteName }}</span>
          </RouterLink>
        </div>

        <!-- 桌面端导航 -->
        <nav class="hidden items-center gap-1 text-sm md:flex md:gap-3">
          <!-- 搜索入口（Ctrl+K） -->
          <button
            class="hidden items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-slate-400 transition hover:border-brand-400/40 hover:text-white sm:flex"
            @click="uiStore.openSearch()">
            <span>🔍</span>
            <span>搜索</span>
            <kbd class="rounded border border-white/10 bg-white/5 px-1.5 py-0.5 text-xs">Ctrl K</kbd>
          </button>
          <RouterLink to="/" class="rounded-lg px-3 py-1.5 text-slate-300 transition hover:bg-white/5 hover:text-white"
            active-class="bg-white/10 text-white">
            画廊
          </RouterLink>
          <RouterLink v-if="userStore.isLogged()" to="/favorites"
            class="rounded-lg px-3 py-1.5 text-slate-300 transition hover:bg-white/5 hover:text-white"
            active-class="bg-white/10 text-white">
            我的收藏
          </RouterLink>
          <RouterLink v-if="userStore.isLogged()" to="/upload"
            class="rounded-lg px-3 py-1.5 text-slate-300 transition hover:bg-white/5 hover:text-white"
            active-class="bg-white/10 text-white">
            上传
          </RouterLink>
          <template v-if="!userStore.isLogged()">
            <RouterLink to="/login"
              class="bg-white/10 text-white router-link-exact-active rounded-lg px-3 py-1.5 text-slate-300 transition hover:bg-white/5 hover:text-white">
              登录
            </RouterLink>
          </template>
          <template v-else>
            <div class="relative" @mouseleave="closeUserMenu">
              <button
                class="flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 p-0.5 hover:border-brand-400/40"
                @mouseenter="openUserMenu">
                <img v-if="userStore.profile?.avatar" :src="userStore.profile.avatar" :alt="userStore.profile.username"
                  class="h-8 w-8 rounded-full object-cover" referrerpolicy="no-referrer" />
                <span v-else
                  class="grid h-8 w-8 place-items-center rounded-full bg-gradient-to-br from-orange-400 to-orange-600 text-sm font-bold text-white">
                  {{ (userStore.profile?.nickname || userStore.profile?.username || 'U').slice(0, 1).toUpperCase() }}
                </span>
              </button>
              <!-- 用户菜单（截图样式：无积分相关内容） -->
              <Transition name="menu">
                <div v-show="userMenuOpen"
                  class="absolute right-0 top-full z-40 mt-2 w-72 origin-top-right rounded-2xl glass p-4 text-sm shadow-2xl backdrop-blur"
                  @mouseenter="openUserMenu">
                  <div class="mb-4 flex items-center gap-3">
                    <img v-if="userStore.profile?.avatar" :src="userStore.profile.avatar"
                      :alt="userStore.profile.username" class="h-12 w-12 shrink-0 rounded-full object-cover"
                      referrerpolicy="no-referrer" />
                    <div v-else
                      class="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-gradient-to-br from-orange-400 to-orange-600 text-base font-bold text-white">
                      {{ (userStore.profile?.nickname || userStore.profile?.username || 'U').slice(0, 1).toUpperCase()
                      }}
                    </div>
                    <div class="min-w-0 flex-1">
                      <p class="truncate text-base font-semibold text-white">
                        {{ userStore.profile?.nickname || userStore.profile?.username || '用户' }}
                      </p>
                      <p class="truncate text-xs text-slate-400">
                        {{ userStore.profile?.email || `@${userStore.profile?.username}` }}
                      </p>
                    </div>
                  </div>

                  <div class="h-px bg-white/10"></div>
                  <div class="space-y-1 py-2">
                    <RouterLink to="/client/account"
                      class="flex items-center gap-3 rounded-lg px-3 py-2.5 text-slate-200 transition hover:bg-white/5"
                      @click="userMenuOpen = false">
                      <span class="grid h-7 w-7 place-items-center rounded-lg bg-white/5 text-base">⚙️</span>
                      <span>账户设置</span>
                    </RouterLink>
                    <RouterLink to="/favorites"
                      class="flex items-center gap-3 rounded-lg px-3 py-2.5 text-slate-200 transition hover:bg-white/5"
                      @click="userMenuOpen = false">
                      <span class="grid h-7 w-7 place-items-center rounded-lg bg-white/5 text-base">❤</span>
                      <span>我的收藏</span>
                    </RouterLink>
                    <RouterLink to="/upload"
                      class="flex items-center gap-3 rounded-lg px-3 py-2.5 text-slate-200 transition hover:bg-white/5"
                      @click="userMenuOpen = false">
                      <span class="grid h-7 w-7 place-items-center rounded-lg bg-white/5 text-base">📤</span>
                      <span>上传案例</span>
                    </RouterLink>
                  </div>

                  <div class="h-px bg-white/10"></div>
                  <button
                    class="mt-2 flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-rose-300 transition hover:bg-rose-500/10"
                    @click="handleLogout">
                    <span class="grid h-7 w-7 place-items-center rounded-lg bg-rose-500/10">⏏</span>
                    <span>退出登录</span>
                  </button>
                </div>
              </Transition>
            </div>
          </template>
        </nav>

        <!-- 移动端右侧：仅保留登录 / 头像入口 -->
        <div class="flex items-center gap-2 md:hidden">
          <template v-if="!userStore.isLogged()">
            <RouterLink to="/login"
              class="bg-white/10 text-white router-link-exact-active rounded-lg px-3 py-1.5 text-slate-300 transition hover:bg-white/5 hover:text-white">
              登录
            </RouterLink>
          </template>
          <template v-else>
            <RouterLink to="/client/account" class="block">
              <img v-if="userStore.profile?.avatar" :src="userStore.profile.avatar" :alt="userStore.profile.username"
                class="h-8 w-8 rounded-full object-cover" referrerpolicy="no-referrer" />
              <span v-else
                class="grid h-8 w-8 place-items-center rounded-full bg-gradient-to-br from-orange-400 to-orange-600 text-sm font-bold text-white">
                {{ (userStore.profile?.nickname || userStore.profile?.username || 'U').slice(0, 1).toUpperCase() }}
              </span>
            </RouterLink>
          </template>
        </div>
      </div>
    </header>

    <!-- 移动端左侧抽屉菜单 -->
    <Transition name="drawer">
      <div v-if="mobileMenuOpen" class="fixed inset-0 z-50 md:hidden" :aria-hidden="!mobileMenuOpen">
        <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="closeMobileMenu"></div>
        <aside class="absolute left-0 top-0 h-full w-72 max-w-[80vw] bg-[#1a1a1a] shadow-2xl"
          style="border-right: 1px solid rgba(255,255,255,0.1)">
          <div class="flex items-center justify-between border-b border-white/10 p-4">
            <span class="text-base font-semibold text-white">菜单</span>
            <button
              class="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition hover:bg-white/10 hover:text-white"
              aria-label="关闭菜单" @click="closeMobileMenu">
              <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <div class="space-y-1 p-4">
            <button
              class="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-slate-200 transition hover:bg-white/5"
              @click="uiStore.openSearch(); closeMobileMenu()">
              <span class="grid h-7 w-7 place-items-center rounded-lg bg-white/5 text-base">🔍</span>
              <span>搜索</span>
              <kbd class="ml-auto rounded border border-white/10 bg-white/5 px-1.5 py-0.5 text-xs text-slate-400">Ctrl
                K</kbd>
            </button>

            <RouterLink to="/"
              class="flex items-center gap-3 rounded-lg px-3 py-2.5 text-slate-200 transition hover:bg-white/5"
              active-class="bg-white/10 text-white" @click="closeMobileMenu">
              <span class="grid h-7 w-7 place-items-center rounded-lg bg-white/5 text-base">🏠</span>
              <span>画廊</span>
            </RouterLink>

            <template v-if="userStore.isLogged()">
              <RouterLink to="/favorites"
                class="flex items-center gap-3 rounded-lg px-3 py-2.5 text-slate-200 transition hover:bg-white/5"
                active-class="bg-white/10 text-white" @click="closeMobileMenu">
                <span class="grid h-7 w-7 place-items-center rounded-lg bg-white/5 text-base">❤</span>
                <span>我的收藏</span>
              </RouterLink>

              <RouterLink to="/upload"
                class="flex items-center gap-3 rounded-lg px-3 py-2.5 text-slate-200 transition hover:bg-white/5"
                active-class="bg-white/10 text-white" @click="closeMobileMenu">
                <span class="grid h-7 w-7 place-items-center rounded-lg bg-white/5 text-base">📤</span>
                <span>上传案例</span>
              </RouterLink>

              <RouterLink to="/client/uploads"
                class="flex items-center gap-3 rounded-lg px-3 py-2.5 text-slate-200 transition hover:bg-white/5"
                active-class="bg-white/10 text-white" @click="closeMobileMenu">
                <span class="grid h-7 w-7 place-items-center rounded-lg bg-white/5 text-base">🗂</span>
                <span>我的上传</span>
              </RouterLink>

              <RouterLink to="/client/account"
                class="flex items-center gap-3 rounded-lg px-3 py-2.5 text-slate-200 transition hover:bg-white/5"
                active-class="bg-white/10 text-white" @click="closeMobileMenu">
                <span class="grid h-7 w-7 place-items-center rounded-lg bg-white/5 text-base">⚙️</span>
                <span>账户设置</span>
              </RouterLink>
            </template>
          </div>

          <div class="absolute bottom-0 left-0 right-0 border-t border-white/10 p-4">
            <template v-if="userStore.isLogged()">
              <button
                class="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-rose-300 transition hover:bg-rose-500/10"
                @click="handleMobileLogout">
                <span class="grid h-7 w-7 place-items-center rounded-lg bg-rose-500/10">⏏</span>
                <span>退出登录</span>
              </button>
            </template>
            <template v-else>
              <RouterLink to="/login"
                class="flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-brand-500 to-pink-500 px-4 py-2.5 text-sm font-medium text-white shadow-md shadow-brand-500/30 hover:from-brand-600 hover:to-pink-600"
                @click="closeMobileMenu">
                登录 / 注册
              </RouterLink>
            </template>
          </div>
        </aside>
      </div>
    </Transition>

    <!-- 路由出口 -->
    <main class="flex-1">
      <RouterView v-slot="{ Component }">
        <transition name="fade" mode="out-in">
          <component :is="Component" />
        </transition>
      </RouterView>
    </main>

    <!-- 页脚 -->
    <footer class="border-t border-white/5 py-6 text-center text-xs text-slate-500">
      <p>
        ©Nsboxs · 仅供学习与灵感参考
      </p>
    </footer>

    <!-- 全局搜索弹窗（Ctrl+K） -->
    <SearchPalette />
    <!-- 回到顶部 -->
    <BackToTop />
  </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.menu-enter-active,
.menu-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.menu-enter-from,
.menu-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(-4px);
}

/* 移动端抽屉：左侧滑入 + 背景淡入淡出 */
.drawer-enter-active,
.drawer-leave-active {
  transition: opacity 0.25s ease;
}

.drawer-enter-active aside,
.drawer-leave-active aside {
  transition: transform 0.25s ease;
}

.drawer-enter-from,
.drawer-leave-to {
  opacity: 0;
}

.drawer-enter-from aside,
.drawer-leave-to aside {
  transform: translateX(-100%);
}

.drawer-enter-to aside,
.drawer-leave-from aside {
  transform: translateX(0);
}
</style>